import { Injectable } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from "rxjs"
import { map, catchError, switchMap, filter, take } from 'rxjs/operators';
import { ToastrService } from "ngx-toastr";
import { Router } from "@angular/router";
import { AuthService } from "./auth.service";
import { InternetConnectionService } from "app/shared/services/internetConnection.service";

@Injectable()

export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  currentUrl: string = '';
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  
    constructor(
      private toastr: ToastrService,
      private router: Router,
      private authService: AuthService,
      private internetConnectionService: InternetConnectionService
    ) {}
  
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.currentUrl = this.router.url;
      // Check for internet connectivity
      if (!navigator.onLine) {
        this.internetConnectionService.setOnlineStatus(false);
        this.toastr.error("You're currently offline. No internet connection");
        return throwError("You're offline. No internet connection");
      }
    if (request.headers.has('skipErrorInterceptor')) {
      return next.handle(request); // Skip the interceptor and proceed with the request
    }
      // Add authentication token to request
    // const token = localStorage.getItem('i-token');
    const token = this.authService.getAccessToken();
      const clonedRequest = this.addTokenHeader(request, token);
  
      return next.handle(clonedRequest).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401 && !request.url.includes('refresh')) {
            // Handle 401 errors with token refresh
            return this.handle401Error(request, next);
          }
  
          // Handle other errors
          return this.handleError(error);
        })
      );
    }
  
    private addTokenHeader(request: HttpRequest<any>, token: string | null): HttpRequest<any> {
      if (token) {
        return request.clone({
          headers: request.headers.set('Authorization', `Bearer ${token}`),
        });
      }
      return request;
    }
  
    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);
  
        return this.authService.refreshToken().pipe(
          switchMap((newToken: string | null) => {
            this.isRefreshing = false;
            if (newToken) {
              this.refreshTokenSubject.next(newToken);
              return next.handle(this.addTokenHeader(request, newToken));
            }
            return this.logoutAndRedirect();
          }),
          catchError(() => {
            this.isRefreshing = false;
            return this.logoutAndRedirect();
          })
        );
      } else {
        return this.refreshTokenSubject.pipe(
          filter((token) => token !== null),
          take(1),
          switchMap((newToken) => next.handle(this.addTokenHeader(request, newToken)))
        );
      }
    }
  
  private logoutAndRedirect(): Observable<HttpEvent<any>> {
    this.authService.storeRedirectUrl(this.currentUrl);
      this.authService.logout();
      this.router.navigate(['/auth/login']);
    this.toastr.info('Please log in to proceed');
      return throwError('Session expired');
    }
  
    private handleError(error: HttpErrorResponse): Observable<never> {
      let errorMessage = '';
  
      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = `Error: ${error.error.message}`;
        this.toastr.error(errorMessage);
      } else {
        // Server-side error
        if (error.status === 0) {
          errorMessage = 'Unable to connect to the server.';
          this.toastr.error(errorMessage, 'Connection Error');
        } else {
          // Show the error message from the server if it exists
          if (error?.error?.message) {
            errorMessage = error.error.message;
          } else {
            errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
          }
          this.toastr.error(errorMessage, 'An error occurred');
        }
      }
  
      return throwError(errorMessage);
    }
  }